import React, { useState, useEffect } from 'react'
import { makeStyles, Typography, Link, Grid, Hidden } from '@material-ui/core'

import { useFiltersContext } from '../../../../contexts/Filters'
import { Exchange } from '../../../../../types/exchange'

import ExchangeCard from './P2PCard'

const useStyles = makeStyles((theme) => ({
  root: {
    padding: '20px 20px',
    margin: 'auto',
    [theme.breakpoints.up('sm')]: {
      padding: '20px 120px 0',
    },
  },
  header: {
    margin: '1rem 0',
    letterSpacing: '-0.02em',
    fontSize: '48px',
    maxWidth: '30%',
    [theme.breakpoints.down('sm')]: {
      fontSize: '48px',
    },
  },
  chip: {
    background: 'rgba(11, 11, 11, 0.8)',
    color: 'white',
  },
  link: {
    color: 'white',
    fontSize: '.8rem',
    padding: '.4rem 0',
    borderBottom: '1px solid white',
  },
  contentSection: {
    margin: '1rem 0',
    '& p': {
      color: 'white',
    },
  },
  contentCard: {
    padding: '1rem',
    background: 'rgba(255, 255, 255, 0.06)',
    borderRadius: '.4rem',
  },
  cardBody: {
    fontSize: '1rem',
  },
  cardHeader: {
    margin: '0 0 1rem 0',
    fontSize: '1.2rem',
    fontWeight: 'bold',
  },
  navContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  linkContainer: {
    display: 'inline-flex',
    flexDirection: 'row',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      margin: '1rem 0',
    },
    '& > a': {
      fontSize: '.84rem',
      color: 'rgba(43, 24, 65, 1)',
      padding: '.4rem 1rem',
      cursor: 'pointer',
      borderRadius: '.8rem',
      margin: '0 .4rem',
      fontWeight: '600',
    },
    '& > a.active': {
      background: 'rgba(0, 0, 0, 0.04)',
      color: 'black',
    },
  },
  selectContainer: {
    minWidth: '11rem',
  },
  cryptoContainer: {
    display: 'flex',
    minWidth: '50%',
  },
  cryptoChip: {
    margin: '0 1rem 0 0',
    padding: '.6rem 1rem',
    borderRadius: '2rem',
    display: 'inline-flex',
    alignItems: 'center',
    color: 'rgba(0,0,0,0.6)',
    fontWeight: 'bold',
    cursor: 'pointer',
    fontSize: '.8rem',
    '& > span': {
      height: '20px',
      width: '20px',
      borderRadius: '50%',
      background: 'rgba(196, 196, 196, 1)',
      display: 'inline-block',
      marginRight: '.4rem',
    },
    '&.active': {
      background: 'rgba(0, 0, 0, 0.04)',
      color: 'rgba(0,0,0,0.9)',
    },
  },
  tableHeader: {
    padding: '1rem 2rem 0',
    margin: '2rem 0 0 0',
    '&>div': {
      padding: '0 !important',
    },
    '& p': {
      fontSize: '.9rem',
    },
  },
  error: {
    padding: '50px',
    margin: '50px',
  },
  exchangeContainer: {
    marginBottom: '0rem',
  },
  exchangesListContainer: {
    padding: '10px 0px',
    [theme.breakpoints.up('md')]: {
      padding: '10px 0px',
    },
    [theme.breakpoints.up('xl')]: {
      margin: 'auto',
    },
  },
}))

interface link {
  label: string
  url: string
}

const navLinks = [
  {
    label: 'Highest Rated',
    url: 'highest-rated',
  },
  {
    label: 'Best Usability',
    url: 'best-usability',
  },
  {
    label: 'Highest Security',
    url: 'highest-security',
  },
] as link[]

interface Props {
  exchanges: Exchange[]
  title?: string
}

function ExchangeItem({ exchange, number }: { exchange: Exchange; number: number }) {
  const classes = useStyles({})
  const context = useFiltersContext()
  return (
    <div className={classes.exchangeContainer}>
      <ExchangeCard
        exchange={exchange}
        cryptocurrency={context.filters.cryptocurrency}
        fiat={context.filters.fiat}
        number={number}
      />
    </div>
  )
}

export default function ExchangesTable({ exchanges, title }: Props): JSX.Element {
  const classes = useStyles({})

  const [activeLink, setActiveLink] = useState(navLinks[0])

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.split('#')[1]
      const activeLink = navLinks.find((link) => link.url === hash) || navLinks[0]
      setActiveLink(activeLink)
    }
    if (!window.onhashchange) {
      window.onhashchange = handleHashChange
    }
    handleHashChange()
  })

  return (
    <div className={classes.root}>
      <nav className={classes.navContainer}>
        <Typography variant="h3" className={classes.header}>
          {title || 'Top 10 peer to peer exchanges'}
        </Typography>
        {/* <div className={classes.linkContainer}>
          {navLinks.map((link, index) => (
            <Link
              href={`#${link.url}`}
              key={index}
              underline="none"
              className={activeLink.url === link.url ? 'active' : ''}
              rel="prefetch"
            >
              {link.label}
            </Link>
          ))}
        </div> */}
      </nav>
      <Grid container alignItems="center" spacing={2} className={classes.tableHeader}>
        <Grid item xs={3}>
          <Typography variant="body2">Exchange</Typography>
        </Grid>
        <Grid item xs={3}>
          <Typography variant="body2">Score</Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography variant="body2">KYC Required?</Typography>
        </Grid>
        <Grid item xs={1}>
          <Typography variant="body2">Cash Trades</Typography>
        </Grid>
        <Hidden smDown>
          <Grid item xs={3}>
            <Typography variant="body2">Assets</Typography>
          </Grid>
        </Hidden>
      </Grid>
      <div className={classes.exchangesListContainer}>
        {exchanges.map((obj: Exchange, index) => (
          <ExchangeItem exchange={obj} key={index} number={index + 1} />
        ))}
      </div>
    </div>
  )
}
